import {
  formatResizeDisplay,
  normalizeConfidenceScore,
  toFiniteCount,
  toFiniteNumber,
} from '../../utils/formatters';
import {
  computeAccuracyRatio,
  formatIdentificationTooltip,
  formatRunDataPoints,
  getItemIdentification,
  getRunCacheStats,
  getRunDataPointsHint,
  getRunIdentificationStats,
  getRunPointStats,
  getRunProgressMetrics,
  getRunTokenTotals,
  getSourceDisplayLabel,
  getResultAttemptSourceInfo,
  getResultTokenCounts,
  withRagPromptPreview,
} from '../../utils/testRigHelpers';

export const getSourceBadgeClassName = (sourceValue, cacheHitValue) => {
  if (cacheHitValue === true) return 'bg-teal/10 text-teal';
  if (cacheHitValue === false) return 'bg-surface-2 text-muted';
  if (sourceValue === 'cache') return 'bg-teal/10 text-teal';
  if (sourceValue === 'provider') return 'bg-surface-2 text-muted';
  return 'bg-warn/10 text-warn';
};

export const formatCostDisplay = (value) => {
  const numericCost = toFiniteNumber(value);
  if (numericCost === null) return 'N/A';
  return `$${numericCost.toFixed(4)}`;
};

export const buildDerivedState = (deps) => {
  const {
    testResults,
    activeRuns,
    runModelSearchQuery,
    runDatasetSearchQuery,
    runMinConfidencePercentById,
    currentPage,
    itemsPerPage,
    selectedDataset,
    isLoadingDatasets,
    datasetLoadError,
    datasets,
    loading,
    codePromptTemplates,
    useEbayRag,
    useNotesRag,
    assumeHiddenItems,
    getFilteredItemEntries,
  } = deps;

  const stats = {
    avgSpeed:
      testResults.length > 0
        ? (
            testResults.reduce(
              (acc, curr) => acc + (curr.avg_speed_ms || curr.avg_speed || 0),
              0,
            ) / testResults.length
          ).toFixed(0)
        : '-',
    avgAccuracy:
      testResults.length > 0
        ? `${(
            (testResults.reduce((acc, curr) => acc + (curr.avg_accuracy || 0), 0) /
              testResults.length) *
            100
          ).toFixed(1)}%`
        : '-',
    totalCost: (() => {
      if (testResults.length === 0) return '-';
      const knownCosts = testResults
        .map((entry) => toFiniteNumber(entry?.total_cost))
        .filter((value) => value !== null);
      if (knownCosts.length === 0) return 'N/A';
      return knownCosts.reduce((acc, value) => acc + value, 0).toFixed(4);
    })(),
    totalRuns: testResults.length,
  };

  const displayedTestResults = [...testResults].sort((a, b) => {
    const safeA = new Date(a?.timestamp || 0).getTime() || 0;
    const safeB = new Date(b?.timestamp || 0).getTime() || 0;
    if (safeA !== safeB) return safeB - safeA;
    return String(b?.run_id || '').localeCompare(String(a?.run_id || ''));
  });
  const normalizedRunModelSearchQuery = runModelSearchQuery.trim().toLowerCase();
  const normalizedRunDatasetSearchQuery = runDatasetSearchQuery.trim().toLowerCase();

  const getRunConfidencePercent = (run = {}, runRowKey = '') => {
    const runKey = String(run?.run_id || runRowKey || '').trim();
    if (!runKey) return 0;
    const rawValue = runMinConfidencePercentById[runKey];
    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue)) return 0;
    return Math.max(0, Math.min(100, Math.round(numericValue)));
  };

  const getRunFilteredAccuracy = (run = {}, minConfidenceThreshold = 0) => {
    if (!(minConfidenceThreshold > 0) || !Array.isArray(run?.results)) return null;
    let accuracySum = 0;
    let accuracyCount = 0;
    run.results.forEach((resultEntry) => {
      const visibleEntries = getFilteredItemEntries(resultEntry?.items, minConfidenceThreshold);
      if (visibleEntries.length === 0) return;
      const estimatedSum = visibleEntries.reduce(
        (sum, entry) => sum + (toFiniteNumber(entry?.item?.estimated) || 0),
        0,
      );
      const actualValue =
        visibleEntries
          .map((entry) => toFiniteNumber(entry?.item?.actual))
          .find((value) => value !== null) ?? null;
      const lotAccuracy = computeAccuracyRatio(actualValue, estimatedSum);
      if (lotAccuracy !== null) {
        accuracySum += lotAccuracy;
        accuracyCount += 1;
      }
    });
    if (accuracyCount <= 0) return null;
    return accuracySum / accuracyCount;
  };

  const runMatchesSearch = (run = {}) => {
    const model = String(run?.model || '').toLowerCase();
    const datasetName = String(run?.dataset_name || '').toLowerCase();
    const datasetPrefix = String(run?.dataset_prefix || '').toLowerCase();
    const modelMatches =
      !normalizedRunModelSearchQuery || model.includes(normalizedRunModelSearchQuery);
    const datasetMatches =
      !normalizedRunDatasetSearchQuery ||
      datasetName.includes(normalizedRunDatasetSearchQuery) ||
      datasetPrefix.includes(normalizedRunDatasetSearchQuery);
    return modelMatches && datasetMatches;
  };

  const filteredActiveRuns = activeRuns.filter(runMatchesSearch);
  const activeRunIdSet = new Set(
    activeRuns.map((run) => String(run?.run_id || '').trim()).filter(Boolean),
  );
  const filteredDisplayedTestResults = displayedTestResults.filter((run) => {
    if (!runMatchesSearch(run)) return false;
    const runId = String(run?.run_id || '').trim();
    if (!runId) return true;
    return !activeRunIdSet.has(runId);
  });

  const totalFilteredTestRuns = filteredDisplayedTestResults.length;
  const totalPages = Math.max(1, Math.ceil(totalFilteredTestRuns / itemsPerPage));
  const indexOfLastRun = currentPage * itemsPerPage;
  const indexOfFirstRun = indexOfLastRun - itemsPerPage;
  const pagedDisplayedTestResults = filteredDisplayedTestResults.slice(
    indexOfFirstRun,
    indexOfLastRun,
  );
  const showingFirstRunNumber = totalFilteredTestRuns > 0 ? indexOfFirstRun + 1 : 0;
  const showingLastRunNumber =
    totalFilteredTestRuns > 0 ? Math.min(indexOfLastRun, totalFilteredTestRuns) : 0;

  const runSearchSummary = [
    normalizedRunModelSearchQuery ? `model "${runModelSearchQuery.trim()}"` : '',
    normalizedRunDatasetSearchQuery
      ? `dataset "${runDatasetSearchQuery.trim()}"`
      : '',
  ]
    .filter(Boolean)
    .join(' and ');

  const primaryActiveRun = activeRuns.length > 0 ? activeRuns[0] : null;
  const primaryRunProgress = primaryActiveRun
    ? getRunProgressMetrics(primaryActiveRun)
    : null;
  const codePromptTemplate = useEbayRag
    ? useNotesRag
      ? codePromptTemplates.ebay_on_notes_on
      : codePromptTemplates.ebay_on_notes_off
    : useNotesRag
      ? codePromptTemplates.ebay_off_notes_on
      : codePromptTemplates.ebay_off_notes_off;
  const visiblePromptTemplate = withRagPromptPreview(
    codePromptTemplate || '',
    useEbayRag,
    useNotesRag,
    assumeHiddenItems,
  );
  const datasetBadgeText = selectedDataset
    ? `${selectedDataset.name}${
        selectedDataset.data_points !== undefined
          ? ` (${selectedDataset.data_points} data points)`
          : ''
      }`
    : isLoadingDatasets
      ? 'Loading datasets...'
      : datasetLoadError
        ? 'Dataset load failed'
        : datasets.length > 0
          ? 'Select a dataset to run'
          : 'No datasets available';

  return {
    stats,
    displayedTestResults,
    normalizedRunModelSearchQuery,
    normalizedRunDatasetSearchQuery,
    getRunConfidencePercent,
    getRunFilteredAccuracy,
    runMatchesSearch,
    filteredActiveRuns,
    activeRunIdSet,
    filteredDisplayedTestResults,
    totalFilteredTestRuns,
    totalPages,
    indexOfLastRun,
    indexOfFirstRun,
    pagedDisplayedTestResults,
    showingFirstRunNumber,
    showingLastRunNumber,
    runSearchSummary,
    primaryActiveRun,
    primaryRunProgress,
    codePromptTemplate,
    visiblePromptTemplate,
    datasetBadgeText,
    getRunPointStats,
    formatRunDataPoints,
    getRunDataPointsHint,
    getRunTokenTotals,
    getRunIdentificationStats,
    getItemIdentification,
    formatIdentificationTooltip,
    getRunCacheStats,
    getResultTokenCounts,
    getResultAttemptSourceInfo,
    getSourceDisplayLabel,
    formatResizeDisplay,
    normalizeConfidenceScore,
    toFiniteCount,
    toFiniteNumber,
  };
};
